package com.oscarherreros.appcalculo.web

import com.oscarherreros.appcalculo.game.Op

/**
 * Los textos de la web. Son los de la app (values-es y values), salvo lo que
 * solo existe aquí: compartir, la app y lo que se guarda en el navegador.
 */
interface Texts {
    /** Idioma para las fechas y el atributo `lang` de la página. */
    val locale: String
    val dailyChallenge: String
    /** El reto [number] en el encabezado: «Reto diario n.º 3». */
    fun numberedChallenge(number: Int): String
    val tagline: String
    val dailyTagline: String
    val target: String
    val pickNumber: String
    val pickOperation: String
    val pickSecondNumber: String
    val stuck: String
    val solvedHint: String
    fun hintNextStep(operation: String): String
    val hintDeadEnd: String
    val undo: String
    val reset: String
    val hint: String
    val hintCost: String
    val steps: String
    val dailySolved: String
    fun solvedInSteps(steps: Int): String
    fun threeStarsTip(steps: Int): String
    fun streak(days: Int): String
    fun doneToday(days: Int): String
    fun starsDescription(earned: Int, max: Int): String
    val replay: String
    val share: String
    val copied: String
    val comeBackTomorrow: String
    fun opName(op: Op): String
    val howToPlay: String
    val howToPlayText: List<String>
    val appPromo: String
    val appComingSoon: String
    val appLink: String
    val storageNote: String
    val privacyPolicy: String
}

private fun plural(count: Int, one: String, other: String) = if (count == 1) one else other

/** Español para quien tenga el navegador en español; inglés para todos los demás, como la app. */
fun textsFor(language: String): Texts = if (language.lowercase().startsWith("es")) Spanish else English

object Spanish : Texts {
    override val locale = "es"
    override val dailyChallenge = "Reto diario"
    override fun numberedChallenge(number: Int) = "Reto diario n.º $number"
    override val tagline = "Combina los números y llega exactamente al objetivo"
    override val dailyTagline = "Un nivel nuevo cada día, el mismo para todos"
    override val target = "Objetivo"
    override val pickNumber = "Toca un número"
    override val pickOperation = "Elige una operación"
    override val pickSecondNumber = "Elige el segundo número"
    override val stuck = "Solo queda una ficha: deshaz o reinicia"
    override val solvedHint = "¡Objetivo conseguido!"
    override fun hintNextStep(operation: String) = "Pista: $operation"
    override val hintDeadEnd = "Así ya no se llega al objetivo: deshaz un paso"
    override val undo = "Deshacer"
    override val reset = "Reiniciar"
    override val hint = "Pista"
    override val hintCost = "Cada pista te quita una estrella"
    override val steps = "Pasos"
    override val dailySolved = "¡Reto completado!"
    override fun solvedInSteps(steps: Int) =
        "Lo has resuelto en $steps " + plural(steps, "operación", "operaciones")
    override fun threeStarsTip(steps: Int) =
        "Consigue las 3 estrellas con $steps " + plural(steps, "operación", "operaciones") + " y sin pistas"
    override fun streak(days: Int) = "Racha: $days " + plural(days, "día", "días")
    override fun doneToday(days: Int) = "Completado hoy · racha de $days " + plural(days, "día", "días")
    override fun starsDescription(earned: Int, max: Int) = "$earned de $max estrellas"
    override val replay = "Repetir"
    override val share = "Compartir"
    override val copied = "Copiado"
    override val comeBackTomorrow = "Mañana hay un reto nuevo"
    override fun opName(op: Op) = when (op) {
        Op.ADD -> "más"
        Op.SUB -> "menos"
        Op.MUL -> "por"
        Op.DIV -> "entre"
    }
    override val howToPlay = "Cómo se juega"
    override val howToPlayText = listOf(
        "Toca un número, después una operación y después otro número: las dos fichas se combinan en una nueva con el resultado.",
        "Sigue operando hasta que una ficha valga exactamente el objetivo. No hace falta usar todos los números.",
        "Los resultados tienen que ser enteros positivos: no valen las restas que den cero o negativo ni las divisiones con decimales.",
        "El reto se puntúa con hasta 3 estrellas: las consigues todas con el mínimo de operaciones y sin pistas.",
        "Cada día hay un reto distinto, el mismo para todos. Complétalo varios días seguidos para alargar tu racha.",
        "También puedes jugar con el teclado: + − * / para las operaciones y la tecla de borrar para deshacer.",
    )
    override val appPromo = "Más niveles y el modo contrarreloj en la app para Android."
    override val appComingSoon = "Próximamente en Google Play."
    override val appLink = "Descárgala en Google Play"
    override val storageNote = "Tu racha y la partida a medias se guardan solo en este navegador."
    override val privacyPolicy = "Política de privacidad"
}

object English : Texts {
    override val locale = "en"
    override val dailyChallenge = "Daily challenge"
    override fun numberedChallenge(number: Int) = "Daily challenge #$number"
    override val tagline = "Combine the numbers to hit the target exactly"
    override val dailyTagline = "A new level every day, the same for everyone"
    override val target = "Target"
    override val pickNumber = "Tap a number"
    override val pickOperation = "Pick an operation"
    override val pickSecondNumber = "Pick the second number"
    override val stuck = "Only one tile left: undo or reset"
    override val solvedHint = "Target reached!"
    override fun hintNextStep(operation: String) = "Hint: $operation"
    override val hintDeadEnd = "The target is out of reach from here: undo a step"
    override val undo = "Undo"
    override val reset = "Reset"
    override val hint = "Hint"
    override val hintCost = "Each hint costs you a star"
    override val steps = "Steps"
    override val dailySolved = "Challenge complete!"
    override fun solvedInSteps(steps: Int) = "Solved in $steps " + plural(steps, "operation", "operations")
    override fun threeStarsTip(steps: Int) =
        "Get all 3 stars with $steps " + plural(steps, "operation", "operations") + " and no hints"
    override fun streak(days: Int) = "Streak: $days " + plural(days, "day", "days")
    override fun doneToday(days: Int) = "Done today · $days-day streak"
    override fun starsDescription(earned: Int, max: Int) = "$earned of $max stars"
    override val replay = "Replay"
    override val share = "Share"
    override val copied = "Copied"
    override val comeBackTomorrow = "A new challenge tomorrow"
    override fun opName(op: Op) = when (op) {
        Op.ADD -> "plus"
        Op.SUB -> "minus"
        Op.MUL -> "times"
        Op.DIV -> "divided by"
    }
    override val howToPlay = "How to play"
    override val howToPlayText = listOf(
        "Tap a number, then an operation, then another number: the two tiles merge into a new one with the result.",
        "Keep going until one tile is exactly the target. You don't have to use every number.",
        "Results must be positive whole numbers: subtractions that give zero or less and divisions with a remainder are not allowed.",
        "The challenge earns up to 3 stars: you get all three with the fewest operations and no hints.",
        "There's a different challenge every day, the same for everyone. Complete it several days in a row to build your streak.",
        "You can also play with the keyboard: + − * / for the operations and backspace to undo.",
    )
    override val appPromo = "More levels and time attack in the Android app."
    override val appComingSoon = "Coming soon to Google Play."
    override val appLink = "Get it on Google Play"
    override val storageNote = "Your streak and unfinished game are saved only in this browser."
    override val privacyPolicy = "Privacy policy"
}
